import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { faSearch, faUpload, faUsers } from "@fortawesome/pro-regular-svg-icons";

import { faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { ButtonStandard, PageBanner } from "@performyard/py-components";

// components
import EmployeeCard from "../../components.smart/EmployeeCard/EmployeeCard";
import Button from "../../components/Button";
import { Grid, GridCol } from "../../components/Grid";
import Icon from "../../components/Icon/Icon";
import Input from "../../components/Inputs/InputText";
import PageHeading from "../../components/PageHeading";
import PageTitle from "../../components/PageTitle";
import Spacer from "../../components/Spacer";
import Table from "../../components/TableLite";
import ManageEmployeeDocumentsModal from "./components/ManageEmployeeDocumentsModal";
import UploadDocumentModal from "./components/UploadDocumentModal";

import { useRbacPrimaryRolePermissions } from "../../api/hooks";
// hooks
import { useDocuments, useManageableDocumentEmployees } from "../../api/hooks/defs/documents";
import { useIsNewNavigation } from "../../api/hooks/defs/navigation";
import useDocumentTable from "./useDocumentTable";
import useDocumentsUserData from "./useDocumentsUserData";

import InfoPageTemplate from "../../pageTemplates/InfoPageTemplate";
import ErrorPage from "../Error";
// styles
import css from "./Documents.module.scss";
import { filterDocuments, useIsCompanyDocumentPage } from "./helpers";
// schema
import { DocumentsData, DocumentsPageProps } from "./schema";

const documentsEmptyState = {
	myFiles: [],
	companyFiles: [],
	signedFiles: [],
	companyMaxSpace: 0,
	companyTotalFilesSize: 0,
};

const Documents = ({ showEmployeeCard = true }: DocumentsPageProps) => {
	const { data: userData, isLoading: isUserDataLoading } = useDocumentsUserData(); // fetch required user data
	const {
		data: documentsData,
		isLoading: isDocumentsLoading,
		error: documentsError,
	} = useDocuments(userData?.employeeID); // fetch user documents data
	const [documents, setDocuments] = useState<DocumentsData>(documentsData || documentsEmptyState); // hold state since we want to modify the data (change filename, delete document)
	const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
	const [showManageEmployeesModal, setShowManageEmployeesModal] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState("");
	const navigate = useNavigate();

	const isCompanyDocumentPage = useIsCompanyDocumentPage();
	const isNewNav = useIsNewNavigation();
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("documents");
	const { data: manageableEmployeesData } = useManageableDocumentEmployees(isNewNav && !isCompanyDocumentPage);

	useEffect(() => {
		setDocuments(documentsData || documentsEmptyState);
	}, [documentsData]);

	const filteredDocuments = filterDocuments(documents, searchValue);

	const {
		columns,
		rows,
		DeleteDocumentModalComponent,
		EditDocumentModalComponent,
		ViewDocumentModalComponent,
		ScanWarningModalComponent,
		EmbeddingTaskWatchersComponent,
		isProcessingDocuments,
	} = useDocumentTable(searchValue.length > 0 ? filteredDocuments : documents, userData, setDocuments);

	if (isUserDataLoading || isDocumentsLoading || isRbacLoading) {
		return <div />;
	}

	if (documentsError?.response?.status) {
		return <ErrorPage code={documentsError.response.status} />;
	}

	const canUpload = !isCompanyDocumentPage || rbacPermissions?.includes("documents:uploadCompanyDocuments");

	const isViewingOtherEmployee =
		!isCompanyDocumentPage &&
		!!userData.employeeID &&
		!!userData.thisUserId &&
		userData.employeeID !== userData.thisUserId;

	const manageableEmployees = manageableEmployeesData?.employees || [];
	const showManageEmployeesButton =
		isNewNav &&
		!isCompanyDocumentPage &&
		(manageableEmployees.length > 0 || !!manageableEmployeesData?.canManageAllEmployees);

	const personalPageTitle =
		isViewingOtherEmployee && userData.employeeName ? `${userData.employeeName}'s Documents` : "My Documents";

	const actionButtons = [
		...(showManageEmployeesButton
			? [
					<ButtonStandard
						data-testid="manageEmployeeDocuments"
						onClick={() => setShowManageEmployeesModal(true)}
						variant="outline"
						color="primary"
						size="medium"
						iconLeft={faUsers}
						key={"manageEmployeeDocumentsButton"}
					>
						{"My Team's Documents"}
					</ButtonStandard>,
				]
			: []),
		...(canUpload
			? [
					<Button
						testid="documentUpload"
						onClick={() => setShowUploadModal(true)}
						color="green"
						className={css.uploadButton}
						key={"uploadButton"}
					>
						<Icon icon={faUpload} size="small" className={css.uploadIcon} />
						Upload Documents
					</Button>,
				]
			: []),
	];

	const viewingOtherEmployeeBanner = isNewNav && isViewingOtherEmployee && (
		<>
			<PageBanner
				icon={faTriangleExclamation}
				size="large"
				color="primary"
				mainTitle={`You're viewing ${userData.employeeName}'s Documents`}
				subTitle={`Anything you upload, edit, or delete here updates ${userData.employeeName}'s Documents page.`}
				rightActionButton={{
					children: "Back to My Documents",
					onClick: () => navigate("/documents"),
				}}
				data-testid="viewingOtherEmployeeBanner"
			/>
			<Spacer height={2} />
		</>
	);

	const toolbarActionRow = (
		<Input
			className={css.searchInput}
			value={searchValue}
			onChange={setSearchValue}
			placeholder="Search tags"
			icon={faSearch}
			clearable
		/>
	);

	const mainContent = (
		<Grid className={css.Grid}>
			<GridCol>
				<Table theme="vJul23" columns={columns} rows={rows} />
			</GridCol>
		</Grid>
	);

	const CompanyDocument = (
		<>
			<PageTitle title={isCompanyDocumentPage ? "Company Documents" : "My Documents"} />
			{isNewNav && <Spacer height={3} />}
			<Grid className={css.Grid} cols={12} gap={3} padding={7}>
				<GridCol span={2} className={!showEmployeeCard ? css.hide : ""}>
					<EmployeeCard employeeID={userData.employeeID} />
				</GridCol>
				<GridCol span={showEmployeeCard ? 10 : 12}>
					{isNewNav && (
						<>
							<PageHeading>Company Documents</PageHeading>
							<Spacer height={2} />
						</>
					)}
					{isProcessingDocuments && rbacPermissions?.includes("documents:editDocument") && (
						<>
							<PageBanner
								icon={faTriangleExclamation}
								size="large"
								color="primary"
								mainTitle="Processing Documents for Ask PerformYard"
								subTitle="We are processing your selected documents for use with Ask PerformYard. This process may take a few minutes so feel free to navigate away from this page and check back later."
								dangerouslySetClassName="max-w-[817px]"
							/>
							<Spacer height={2} />
						</>
					)}
					<div className={css.tableUtilsContainer}>
						<div className={css.controlsLeft}>{toolbarActionRow}</div>
						{canUpload && <div className={css.controlsRight}>{actionButtons}</div>}
					</div>
					<Spacer height={2} />
					<Table theme="vJul23" columns={columns} rows={rows} />
				</GridCol>
			</Grid>
		</>
	);

	return (
		<>
			{isCompanyDocumentPage ? (
				CompanyDocument
			) : (
				<InfoPageTemplate
					actionButtons={actionButtons}
					toolbarActionRow={toolbarActionRow}
					toolbarInformationRow={<></>}
					drawer={undefined}
					mainContent={mainContent}
					pageTitle={isNewNav ? personalPageTitle : "My Documents"}
					pageHeading={
						isNewNav ? (
							<>
								{viewingOtherEmployeeBanner}
								<PageHeading>{personalPageTitle}</PageHeading>
							</>
						) : undefined
					}
					employeeID={showEmployeeCard ? userData.employeeID : undefined}
				/>
			)}
			{DeleteDocumentModalComponent}
			{EditDocumentModalComponent}
			{ViewDocumentModalComponent}
			{ScanWarningModalComponent}
			<UploadDocumentModal
				show={showUploadModal}
				onClose={() => setShowUploadModal(false)}
				userData={userData}
				setDocuments={setDocuments}
				documents={documents}
			/>
			<ManageEmployeeDocumentsModal
				isOpen={showManageEmployeesModal}
				onClose={() => setShowManageEmployeesModal(false)}
				employees={manageableEmployees}
			/>
			{EmbeddingTaskWatchersComponent}
		</>
	);
};

export default Documents;
